/**
 * Returns a list of all Text Nodes inside the element, even from child nodes.
 *
 * @param {Node} element
 * @return {[Node]}
 */
export function getTextNodes(element) {
	const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
	const nodes = [];

	let currentNode = walker.currentNode;
	while(currentNode) {
		if(currentNode.nodeType === 3)
			nodes.push(currentNode);
		currentNode = walker.nextNode();
	}

	return nodes;
}

/**
 * Returns a list of all lines of actual browser rendered line breaks using the Range API.
 *
 * Each character will be selected and compared with the previous character y offset, if it is higher then a new line
 * appeared.
 *
 * @param {Node} element
 * @param {Number=} maxLines Limit lines to find.
 * @return {[{ y: Number, height: Number, text: String }]}
 */
export function getLineBreaks(element, maxLines = undefined) {
	// Range object from which we will get the characters position.
	const range = document.createRange();
	const lines = [];
	const fullText = element.textContent;
	const textNodes = getTextNodes(element);

	let nodeIndex = 0;
	let lastGlobalCharIndex = 0; // last char index of full text (all nodes)
	let globalCharIndex = 0; // char index of full text (all nodes)
	let lastCharY = undefined;
	let charY = undefined;
	let charHeight = 0;

	// Iterate over all text nodes.
	while(nodeIndex < textNodes.length) {
		const node = textNodes[nodeIndex];
		const nodeText = node.textContent;
		let charIndex = 0; // char index per node

		// Iterate over all characters
		while(charIndex < nodeText.length) {
			// Move our cursor
			range.setStart(node, charIndex);
			if(charIndex <= nodeText.length - 1)
				range.setEnd(node, charIndex + 1);

			const charRect = range.getBoundingClientRect();

			if(lastCharY === undefined)
				lastCharY = charRect.top;
			charY = charRect.top;

			if(charRect.height > charHeight)
				charHeight = charRect.height + 1;

			if(charY > lastCharY) {
				lines.push({
					y: lastCharY,
					height: charHeight,
					text: fullText.substr(lastGlobalCharIndex, globalCharIndex - lastGlobalCharIndex)
				});

				if(maxLines && lines.length >= maxLines)
					return lines;

				lastCharY = charY;
				lastGlobalCharIndex = globalCharIndex;
				charHeight = 0;
			}

			charIndex++;
			globalCharIndex++;
		}

		nodeIndex++;
	}

	if(charY !== undefined) {
		const node = textNodes[nodeIndex-1];
		const nodeText = node.textContent;
		let charIndex = nodeText.length;

		// Move our cursor
		range.setStart(node, charIndex);
		if(charIndex <= node.textContent.length - 1)
			range.setEnd(node, charIndex + 1);

		lines.push({
			y: charY,
			height: range.getBoundingClientRect().height + 1,
			text: fullText.substr(lastGlobalCharIndex, globalCharIndex - lastGlobalCharIndex)
		});
	}

	return lines;
}

/**
 * Will truncate to maximal characters and keeps html tags intact as long they are not empty.
 * Returns a new DocumentFragment with the cloned and truncated html.
 *
 * @param {Node} element
 * @param {Number} maxChars
 * @param {string?} ellipsis
 * @return {DocumentFragment}
 */
export function truncateHTML(element, maxChars, ellipsis = "...") {
	maxChars -= ellipsis.length;
	let chars = 0;

	const traverse = (element) => {
		const frag = document.createDocumentFragment();

		for(let i = 0, len = element.childNodes.length; i < len; i++) {
			const node = element.childNodes[i];

			if(node.nodeType === Node.TEXT_NODE) {
				const n = node.cloneNode();
				if(chars + n.length <= maxChars) {
					frag.appendChild(n);
					chars += n.length;
				} else {
					// Text is to long, try to truncate it, otherwise discard it entirely
					const overflow = chars + n.length - maxChars;
					if(overflow < n.length) {
						n.nodeValue = n.nodeValue.substr(0, n.length - overflow).trimRight();
						frag.appendChild(n);
						chars += n.length;
					}
					break;
				}
			} else if(node.nodeType === Node.ELEMENT_NODE) {
				if(node.hasChildNodes()) {
					const childFrag = traverse(node);
					// Only add if children are not empty
					if(childFrag.hasChildNodes()) {
						const n = node.cloneNode();
						n.appendChild(childFrag);
						frag.appendChild(n);
					}
				}
			}

			if(chars >= maxChars)
				break;
		}

		return frag;
	};

	const truncated = traverse(element);

	// Adding ellipsis
	truncated.appendChild(document.createTextNode(ellipsis));

	return truncated;
}